import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/pdf.worker.entry';
import { Button, ButtonGroup, Tooltip, Dialog, DialogContent, IconButton, AppBar, Toolbar } from '@mui/material';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import styled from '@emotion/styled';
import { FileBase64Response } from '../../model/base/response/file-base-64-response';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';



export default function DefaultPreviewPdfFile({ fileUrl }: { fileUrl: string }) {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [file, setFile] = useState<string>();
    const [useFallback, setUseFallback] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    const PDFDocumentWrapper = styled.div`
    canvas {
      width: 100% !important;
      height: auto !important;
    }
  `;

    const FullscreenPDFWrapper = styled.div`
    canvas {
      width: 100% !important;
      height: auto !important;
      max-height: calc(100vh - 100px) !important;
    }
  `;
    useEffect(() => {
        if (fileUrl !== undefined) {
            // Primeiro tenta carregar diretamente
            setFile(fileUrl);
        }
    }, [fileUrl]);

    const onDocumentLoadSuccess = (props: any) => {
        setNumPages(props._pdfInfo.numPages);
        setUseFallback(false);
    };

    const onDocumentLoadError = (error: any) => {
        console.error('Erro ao carregar PDF:', error);
        setUseFallback(true);
    };
    const goToPrevPage = () =>
        setPageNumber(pageNumber - 1 <= 1 ? 1 : pageNumber - 1);

    const gotToFirstPage = () =>
        setPageNumber(1);


    const goToNextPage = () =>
        setPageNumber(
            pageNumber + 1 >= numPages ? numPages : pageNumber + 1,
        );

    const gotToLastPage = () =>
        setPageNumber(numPages);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleCloseFullscreen = () => {
        setIsFullscreen(false);
    };

    return (
        <Grid container spacing={0} sx={{
            backgroundColor: 'lightgray'
        }}>
            <Grid item xs={12} md={12} lg={12} sx={{
                m: 2,
                p: 2, display: "flex", justifyContent: 'center', backgroundColor: 'white'
            }}>
                {!useFallback && (
                    <>
                        <Grid item>
                            <ButtonGroup
                                disableElevation
                                variant="contained"
                            >
                                <Tooltip title="Primeira página">
                                    <Button disabled={pageNumber === 1}
                                        onClick={gotToFirstPage}>
                                        <KeyboardDoubleArrowLeftIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Página anterior">
                                    <Button variant="contained"
                                        disabled={pageNumber === 1}
                                        onClick={goToPrevPage}>
                                        <KeyboardArrowLeftIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                        <Grid item xs sx={{ display: 'flex', textAlign: 'center', verticalAlign: 'center', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography variant="button" sx={{ display: 'block' }} gutterBottom>Página {pageNumber} de {numPages}</Typography>
                        </Grid>
                        <Grid item sx={{ textAlign: 'right' }}>
                            <ButtonGroup
                                disableElevation
                                variant="contained"
                            >
                                <Tooltip title="Próxima página">
                                    <Button variant="contained"
                                        disabled={pageNumber === numPages}
                                        onClick={goToNextPage}>
                                        <KeyboardArrowRightIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Última página">
                                    <Button disabled={pageNumber === numPages}
                                        onClick={gotToLastPage}>
                                        <KeyboardDoubleArrowRightIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                    </>
                )}

                <Grid item xs justifyContent="flex-end" sx={{ textAlign: 'right' }}>
                    <ButtonGroup variant="contained">
                        <Tooltip title="Ver em tela cheia">
                            <Button onClick={toggleFullscreen}>
                                <FullscreenIcon />
                            </Button>
                        </Tooltip>
                        <Tooltip title="Baixar o arquivo">
                            <Button onClick={() => {
                                if (file !== undefined && file !== null) {
                                    const downloadLink = document.createElement("a");
                                    downloadLink.href = fileUrl;
                                    downloadLink.target = "_blank";
                                    downloadLink.click();
                                }
                            }} >
                                <DownloadIcon />
                            </Button>
                        </Tooltip>
                    </ButtonGroup>
                </Grid>
            </Grid>
            <Grid item xs={12} md={12} lg={12} sx={{
                p: 2, backgroundColor: 'lightgray'
            }}>
                {file !== undefined && !useFallback && (
                    <PDFDocumentWrapper>
                        <Document
                            file={file}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            options={{
                                cMapUrl: 'cmaps/',
                                cMapPacked: true,
                                standardFontDataUrl: 'standard_fonts/',
                            }}
                        >
                            <Page pageNumber={pageNumber} />
                        </Document>
                    </PDFDocumentWrapper>
                )}
                {useFallback && (
                    <div style={{ width: '100%', height: '600px' }}>
                        <iframe
                            src={fileUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title="Visualizador de PDF"
                        />
                    </div>
                )}
            </Grid>

            {/* Modal de tela cheia */}
            <Dialog
                open={isFullscreen}
                onClose={handleCloseFullscreen}
                maxWidth={false}
                fullScreen
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: '#f5f5f5'
                    }
                }}
            >
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Visualizar PDF - Página {pageNumber} de {numPages}
                        </Typography>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            {/* Controles de navegação */}
                            <ButtonGroup variant="outlined" sx={{ backgroundColor: 'white' }}>
                                <Tooltip title="Primeira página">
                                    <Button
                                        disabled={pageNumber === 1}
                                        onClick={gotToFirstPage}
                                        sx={{ color: 'primary.main' }}
                                    >
                                        <KeyboardDoubleArrowLeftIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Página anterior">
                                    <Button
                                        disabled={pageNumber === 1}
                                        onClick={goToPrevPage}
                                        sx={{ color: 'primary.main' }}
                                    >
                                        <KeyboardArrowLeftIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Próxima página">
                                    <Button
                                        disabled={pageNumber === numPages}
                                        onClick={goToNextPage}
                                        sx={{ color: 'primary.main' }}
                                    >
                                        <KeyboardArrowRightIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Última página">
                                    <Button
                                        disabled={pageNumber === numPages}
                                        onClick={gotToLastPage}
                                        sx={{ color: 'primary.main' }}
                                    >
                                        <KeyboardDoubleArrowRightIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                            <Tooltip title="Sair da tela cheia">
                                <IconButton
                                    color="inherit"
                                    onClick={handleCloseFullscreen}
                                    aria-label="sair da tela cheia"
                                >
                                    <FullscreenExitIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Fechar">
                                <IconButton
                                    color="inherit"
                                    onClick={handleCloseFullscreen}
                                    aria-label="fechar"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </Toolbar>
                </AppBar>
                <DialogContent
                    sx={{
                        padding: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#f5f5f5',
                        overflow: 'auto'
                    }}
                >
                    {file !== undefined && !useFallback && (
                        <FullscreenPDFWrapper>
                            <Document
                                file={file}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                options={{
                                    cMapUrl: 'cmaps/',
                                    cMapPacked: true,
                                    standardFontDataUrl: 'standard_fonts/',
                                }}
                            >
                                <Page pageNumber={pageNumber} />
                            </Document>
                        </FullscreenPDFWrapper>
                    )}
                    {useFallback && (
                        <div style={{ width: '100%', height: '100%' }}>
                            <iframe
                                src={fileUrl}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                title="Visualizador de PDF"
                            />
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Grid>);
}